import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { memo } from 'react';

import { userLib } from 'entities/user';

import { EUserOnboardingStage, Pagination, UserPlatform, UserResponse } from 'shared/api';

type Column = {
  id: string;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'email', label: 'Email', sortable: true, align: 'left' },
  { id: 'name', label: 'Name', sortable: true, align: 'left' },
  { id: 'country', label: 'Country', sortable: true, align: 'left' },
  { id: 'language', label: 'App Language', sortable: false, align: 'center' },
  { id: 'platform', label: 'Platform', sortable: false, align: 'left' },
  { id: 'stage', label: 'Stage', sortable: true, align: 'left' },
  { id: 'peoplesCount', label: 'Peoples', sortable: true, align: 'right' },
  { id: 'pets', label: 'Pets', sortable: false, align: 'right' },
  { id: 'vaccinesCount', label: 'Vaccines', sortable: false, align: 'right' },
  { id: 'dateOfRegistration', label: 'Registration', sortable: true, align: 'left' },
  { id: 'lastLogin', label: 'Last Seen', sortable: true, align: 'left' },
  { id: 'descriptions', label: 'Comments', sortable: false, align: 'left' },
];

const PLATFORM_LABELS: Record<UserPlatform, string> = {
  ios: 'iOS',
  android: 'Android',
};

const formatPlatform = (platform: UserPlatform | null): string => (platform ? PLATFORM_LABELS[platform] : '—');

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const StageCell = ({ stage }: { stage: EUserOnboardingStage }) => {
  const meta = userLib.STAGE_META[stage];

  // stage обязателен по контракту, но страхуемся от неизвестного значения с бека
  if (!meta) {
    return <TableCell align="left">{stage ?? '—'}</TableCell>;
  }

  return (
    <TableCell align="left">
      <Tooltip arrow title={meta.description}>
        <Chip label={meta.label} color={meta.color} size="small" variant="outlined" sx={{ cursor: 'help' }} />
      </Tooltip>
    </TableCell>
  );
};

const PetsCell = ({ dogs, cats }: { dogs: number; cats: number }) => {
  const total = dogs + cats;

  if (total === 0) {
    return <TableCell align="right">0</TableCell>;
  }

  return (
    <TableCell align="right">
      <Tooltip
        arrow
        title={
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              columnGap: 1.5,
              rowGap: 0.25,
            }}
          >
            <span>Dogs</span>
            <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{dogs}</span>
            <span>Cats</span>
            <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{cats}</span>
          </Box>
        }
      >
        <Box
          component="span"
          aria-label={`${total} pets: ${dogs} dogs, ${cats} cats`}
          sx={{ cursor: 'help', fontVariantNumeric: 'tabular-nums' }}
        >
          {total}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

type Props = {
  users: UserResponse[] | undefined;
  openUser: (id: string) => void;
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (sortBy: string) => void;
};
export const UsersTable = memo(
  ({
    users,
    openUser,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
  }: Props) => {
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1); // MUI использует 0-based, API использует 1-based
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Индикатор загрузки */}
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        {/* Затемнение таблицы при загрузке */}
        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => handleSortRequest(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users && users.length > 0 ? (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        ...userLib.getRoleRowSx(user.role),
                      }}
                      hover
                      onClick={() => openUser(user.id)}
                    >
                      <TableCell align="left">{user.email}</TableCell>
                      <TableCell align="left">{user.name ?? '—'}</TableCell>
                      <TableCell align="left">{user.country?.name ?? '—'}</TableCell>
                      <TableCell align="center">{user.language ?? '—'}</TableCell>
                      <TableCell align="left">{formatPlatform(user.platform)}</TableCell>
                      <StageCell stage={user.stage} />
                      <TableCell align="right">{user.peoplesCount}</TableCell>
                      <PetsCell dogs={user.dogsCount} cats={user.catsCount} />
                      <TableCell align="right">{user.vaccinesCount}</TableCell>
                      <TableCell align="left">{formatDate(user.dateOfRegistration)}</TableCell>
                      <TableCell align="left">{formatDate(user.lastLogin)}</TableCell>
                      <TableCell align="left">{user.descriptions}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {pagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1} // MUI использует 0-based
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

UsersTable.displayName = 'UsersTable';
